import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type ButtonHTMLAttributes, type ReactElement } from 'react';

import { cn } from '../lib/cn.js';

/**
 * Variants do Button. Toda classe usa tokens semânticos de
 * @kmgestao/config-tailwind — nunca hex/valor literal.
 *
 * - default  → botão primário (ação principal)
 * - secondary→ botão secundário (ação alternativa neutra)
 * - destructive → ações irreversíveis (delete, cancel trial)
 * - outline  → mesma hierarquia que secondary, borda em vez de fill
 * - ghost    → invisível até hover; use para ações inline em listas
 * - link     → estilo de hyperlink (underline no hover)
 *
 * Tamanhos:
 * - default (h-10) = 40px → alvo de toque ok em mobile
 * - sm (h-9)       → listas densas no desktop
 * - lg (h-11)      = 44px → preferencial em mobile-primary (CTA login)
 * - icon (h-10 w-10) → botões só de ícone
 */
export const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 active:scale-[0.98]',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-sm',
        outline:
          'border border-input bg-background hover:bg-accent hover:text-accent-foreground shadow-sm',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-6 text-base',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  /**
   * Renderiza o botão no elemento filho (Radix Slot). Útil para compor com
   * <Link>, <a> ou outros wrappers sem perder as classes.
   *
   * @example
   * <Button asChild><Link to="/foo">Ir</Link></Button>
   */
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, asChild = false, ...props },
  ref,
): ReactElement {
  const Comp = asChild ? Slot : 'button';
  return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
});
